<template>
  <Teleport to="#modalTarget">
    <div 
      v-if="showModal"
      class="modal show" 
      id="assignTemplateModal" 
      tabindex="-1" 
      aria-labelledby="assignTemplateModalLabel" 
      aria-hidden="false"
      style="display: block;"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="assignTemplateModalLabel">Template zuweisen</h5>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Search Bar -->
          <div class="mb-4">
            <div class="input-group">
              <span class="input-group-text">
                <font-awesome-icon icon="search" />
              </span>
              <input 
                type="text" 
                class="form-control" 
                placeholder="Templates durchsuchen..."
                v-model="searchQuery"
                @input="filterTemplates"
              >
            </div>
          </div>

          <!-- Category Filter -->
          <div class="mb-4">
            <div class="d-flex flex-wrap gap-2">
              <button 
                v-for="category in availableCategories" 
                :key="category.id"
                class="btn btn-outline-secondary btn-sm"
                :class="{ 'active': selectedCategory === category.id }"
                @click="selectCategory(category.id)"
              >
                {{ category.name }}
              </button>
              <button 
                class="btn btn-outline-secondary btn-sm"
                :class="{ 'active': selectedCategory === null }"
                @click="selectCategory(null)"
              >
                Alle
              </button>
            </div>
          </div>

          <!-- Template Cards -->
          <div class="row g-3">
            <div 
              v-for="template in filteredTemplates" 
              :key="template.id"
              class="col-md-6 col-lg-4"
            >
              <div 
                class="card template-card h-100"
                :class="{ 'selected': selectedTemplateId === template.id }"
                @click="selectTemplate(template)"
              >
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-start mb-2">
                    <h6 class="card-title mb-0">{{ template.displayName }}</h6>
                    <div class="template-badges">
                      <span v-if="template.isProFeature" class="badge bg-warning text-dark me-1">
                        <font-awesome-icon icon="crown" />
                      </span>
                      <span v-if="template.isDefault" class="badge bg-success me-1">
                        <font-awesome-icon icon="star" />
                      </span>
                    </div>
                  </div>
                  
                  <p class="card-text small text-muted mb-2">
                    {{ template.description || 'Keine Beschreibung verfügbar' }}
                  </p>
                  
                  <div class="template-categories mb-2">
                    <small class="text-muted">
                      <strong>Bereiche:</strong><br>
                      <span v-if="template.personalCategoryName">
                        • {{ template.personalCategoryName }}
                      </span>
                      <span v-else>• Persönliche Daten</span><br>
                      <span v-if="template.receiptCategoryName">
                        • {{ template.receiptCategoryName }}
                      </span>
                      <span v-else>• Quittungsdaten</span><br>
                      <span v-if="template.financialCategoryName">
                        • {{ template.financialCategoryName }}
                      </span>
                      <span v-else>• Finanzielle Daten</span>
                    </small>
                  </div>

                  <div class="template-features">
                    <span v-if="template.allowCustomFields" class="badge bg-info me-1">
                      <font-awesome-icon icon="edit" />
                      Custom Fields
                    </span>
                  </div>
                </div>
                
                <div class="card-footer bg-transparent">
                  <div class="form-check">
                    <input 
                      :id="`template-${template.id}`"
                      :checked="selectedTemplateId === template.id"
                      type="radio"
                      class="form-check-input"
                      name="templateSelection"
                      @change="selectTemplate(template)"
                      @click="selectTemplate(template)"
                    >
                    <label :for="`template-${template.id}`" class="form-check-label">
                      Auswählen
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- No Results -->
          <div v-if="filteredTemplates.length === 0" class="text-center py-4">
            <font-awesome-icon icon="search" size="2x" class="text-muted mb-3" />
            <p class="text-muted">Keine Templates gefunden</p>
          </div>
        </div>

        <!-- Template Options -->
        <div class="modal-body border-top">
          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label for="customName" class="form-label">Benutzerdefinierter Name (optional)</label>
                <input 
                  type="text" 
                  class="form-control" 
                  id="customName" 
                  v-model="customName"
                  placeholder="Angepasster Name für den Bereich"
                >
                <div class="form-text">
                  Überschreibt den Standard-Namen des Templates für diesen Bereich.
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <div class="form-check">
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="isDefault" 
                    v-model="isDefault"
                  >
                  <label class="form-check-label" for="isDefault">
                    Als Standard-Template setzen
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeModal">Abbrechen</button>
          <button 
            type="button" 
            class="btn btn-primary" 
            @click="assignTemplate"
            :disabled="!canAssign || isAssigning"
          >
            <span v-if="isAssigning" class="spinner-border spinner-border-sm me-2" role="status"></span>
            Template zuweisen
          </button>
        </div>
      </div>
    </div>
    </div>
    
    <!-- Modal Backdrop -->
    <div v-if="showModal" class="modal-backdrop fade show" @click="closeModal"></div>
  </Teleport>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { templateService } from '@/api/templates'
import { showError } from '@/services/notification'

export default {
  name: 'AssignTemplateModal',
  props: {
    teamId: {
      type: String,
      required: true
    },
    showModal: {
      type: Boolean,
      default: false
    }
  },
  emits: ['template-assigned', 'modal-closed'],
  setup(props, { emit }) {
    const availableTemplates = ref([])
    const availableCategories = ref([])
    const selectedTemplateId = ref('')
    const selectedCategory = ref(null)
    const searchQuery = ref('')
    const customName = ref('')
    const isDefault = ref(false)
    const isAssigning = ref(false)

    const selectedTemplate = computed(() => {
      return availableTemplates.value.find(t => t.id === selectedTemplateId.value)
    })

    const filteredTemplates = computed(() => {
      let templates = availableTemplates.value

      
      if (selectedCategory.value !== null) {
        templates = templates.filter(template => template.categoryId === selectedCategory.value)
      }

      
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase()
        templates = templates.filter(template => 
          template.displayName.toLowerCase().includes(query) ||
          template.description?.toLowerCase().includes(query) ||
          template.name.toLowerCase().includes(query)
        )
      }

      return templates
    })

    const canAssign = computed(() => {
      return selectedTemplateId.value
    })

    const loadTemplates = async () => {
      try {
        const response = await templateService.getAllTemplates()
        availableTemplates.value = response.data || []
      } catch (error) {
        console.error('Fehler beim Laden der Templates:', error)
      }
    }

    const loadCategories = async () => {
      try {
        const response = await templateService.getAllCategories()
        availableCategories.value = response.data || []
      } catch (error) {
        console.error('Fehler beim Laden der Kategorien:', error)
      }
    }

    const selectCategory = (categoryId) => {
      selectedCategory.value = categoryId
    }

    const selectTemplate = (template) => {
      selectedTemplateId.value = template.id
    }

    const filterTemplates = () => {
      
    }

    const closeModal = () => {
      
      emit('modal-closed')
    }

    const assignTemplate = async () => {
      isAssigning.value = true
      
      try {
        await templateService.assignTemplateToTeam(
          props.teamId,
          selectedTemplateId.value,
          isDefault.value,
          customName.value.trim() || null
        )

        
        selectedTemplateId.value = ''
        customName.value = ''
        isDefault.value = false

        
        emit('modal-closed')

        
        emit('template-assigned')
      } catch (error) {
        console.error('Fehler beim Zuweisen des Templates:', error)
        showError(error.response?.data?.error || 'Fehler beim Zuweisen des Templates')
      } finally {
        isAssigning.value = false
      }
    }

    onMounted(() => {
      loadTemplates()
      loadCategories()
    })

    return {
      availableTemplates,
      availableCategories,
      selectedTemplateId,
      selectedCategory,
      searchQuery,
      filteredTemplates,
      customName,
      isDefault,
      isAssigning,
      selectedTemplate,
      canAssign,
      selectCategory,
      selectTemplate,
      filterTemplates,
      assignTemplate,
      closeModal
    }
  }
}
</script>
